import { FC } from 'react';
import { Eye, Edit, Trash2 } from 'lucide-react';
import { DataTable, Column } from '../DataTable';
import { StatusBadge } from '../StatusBadge';
import { formatCurrencyAmount } from '../../../utils/currency';
import type { AdminService, Pagination } from '../../../types/admin.types';

interface ServiceTableProps {
  services: AdminService[];
  pagination: Pagination | null;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onView: (service: AdminService) => void;
  onEdit: (service: AdminService) => void;
  onDelete: (service: AdminService) => void;
  onToggleStatus: (service: AdminService) => void;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

export const ServiceTable: FC<ServiceTableProps> = ({
  services,
  pagination,
  isLoading,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const columns: Column<AdminService>[] = [
    {
      key: 'name',
      header: 'Service',
      render: (service) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-slate-100">{service.name}</p>
          <p className="text-sm text-gray-500 dark:text-slate-400">{service.category}</p>
        </div>
      ),
    },
    {
      key: 'duration',
      header: 'Duration',
      render: (service) => (
        <span className="text-gray-700 dark:text-slate-300">{formatDuration(service.duration)}</span>
      ),
    },
    {
      key: 'basePrice',
      header: 'Price',
      render: (service) => (
        <div>
          <p className="text-gray-900 dark:text-slate-100">{formatCurrencyAmount(service.basePrice)}</p>
          {service.depositAmount && (
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Deposit: {formatCurrencyAmount(service.depositAmount)}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'displayOrder',
      header: 'Order',
      render: (service) => (
        <span className="text-gray-600 dark:text-slate-400">{service.displayOrder}</span>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (service) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleStatus(service);
          }}
          className="cursor-pointer"
        >
          <StatusBadge
            status={service.isActive ? 'active' : 'inactive'}
            variant={service.isActive ? 'success' : 'default'}
          />
        </button>
      ),
    },
  ];

  const renderActions = (service: AdminService) => (
    <>
      <button
        onClick={() => onView(service)}
        className="rounded p-1.5 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-slate-200"
        title="View details"
        aria-label="View"
      >
        <Eye className="h-4 w-4" />
      </button>
      <button
        onClick={() => onEdit(service)}
        className="rounded p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-300"
        title="Edit service"
        aria-label="Edit"
      >
        <Edit className="h-4 w-4" />
      </button>
      <button
        onClick={() => onDelete(service)}
        className="rounded p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-300"
        title="Delete service"
        aria-label="Delete"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </>
  );

  return (
    <DataTable
      columns={columns}
      data={services}
      keyExtractor={(s) => s.id}
      isLoading={isLoading}
      emptyMessage="No services found"
      pagination={pagination ?? undefined}
      onPageChange={onPageChange}
      actions={renderActions}
    />
  );
};

export default ServiceTable;
